/**
 * ZAC at work, while a request is read and planned (Sergiu, 2026-11-03: "what you think ZAC looks like while
 * working"): a Zoom-blue robot in a headset, typing on its laptop, its antenna blinking, a gear turning over its head
 * and z's drifting up because it is zacking. Drawn and moved in SVG and CSS; still for a reader who asks for less
 * motion.
 */
export function ZacMascot({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 140" role="img" aria-label="ZAC, a small blue robot in a headset, typing away on its laptop" className={className}>
      <style>{`
        .zm-arm-l { animation: zm-type 0.32s ease-in-out infinite alternate; transform-origin: 74px 92px; }
        .zm-arm-r { animation: zm-type 0.32s ease-in-out infinite alternate-reverse; transform-origin: 126px 92px; }
        .zm-eyes { animation: zm-blink 4.2s infinite; transform-origin: 100px 58px; }
        .zm-bulb { animation: zm-glow 1.1s ease-in-out infinite alternate; }
        .zm-gear { animation: zm-spin 3s linear infinite; transform-origin: 156px 30px; }
        .zm-body { animation: zm-bob 1.6s ease-in-out infinite; }
        .zm-z { animation: zm-rise 2.4s ease-in infinite; opacity: 0; }
        .zm-z2 { animation-delay: 0.8s; }
        .zm-z3 { animation-delay: 1.6s; }
        .zm-screen { animation: zm-flicker 0.9s steps(2) infinite; }
        @keyframes zm-type { from { transform: rotate(-8deg); } to { transform: rotate(10deg); } }
        @keyframes zm-blink { 0%, 92%, 100% { transform: scaleY(1); } 95% { transform: scaleY(0.1); } }
        @keyframes zm-glow { from { fill: #7dd3fc; } to { fill: #fde047; } }
        @keyframes zm-spin { to { transform: rotate(360deg); } }
        @keyframes zm-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
        @keyframes zm-rise { 0% { opacity: 0; transform: translate(0, 0) scale(0.6); } 20% { opacity: 1; } 100% { opacity: 0; transform: translate(14px, -34px) scale(1.2); } }
        @keyframes zm-flicker { 0% { opacity: 1; } 50% { opacity: 0.55; } }
        @media (prefers-reduced-motion: reduce) { .zm-arm-l, .zm-arm-r, .zm-eyes, .zm-bulb, .zm-gear, .zm-body, .zm-z, .zm-screen { animation: none; } .zm-z { opacity: 0.8; } }
      `}</style>

      {/* the desk */}
      <rect x="20" y="118" width="160" height="6" rx="3" fill="currentColor" opacity="0.18" />

      <g className="zm-body">
        {/* antenna */}
        <line x1="100" y1="22" x2="100" y2="34" stroke="#1e3a8a" strokeWidth="3" strokeLinecap="round" />
        <circle className="zm-bulb" cx="100" cy="19" r="5" fill="#7dd3fc" />

        {/* head, its screen face, the headset */}
        <rect x="70" y="34" width="60" height="46" rx="14" fill="#2D8CFF" />
        <rect x="78" y="42" width="44" height="30" rx="9" fill="#0b1f44" />
        <g className="zm-eyes">
          <circle cx="91" cy="56" r="4" fill="#7dd3fc" />
          <circle cx="109" cy="56" r="4" fill="#7dd3fc" />
        </g>
        <path d="M92 65 Q100 70 108 65" stroke="#7dd3fc" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <path d="M68 58 Q68 28 100 28 Q132 28 132 58" stroke="#0f172a" strokeWidth="4" fill="none" strokeLinecap="round" />
        <rect x="63" y="52" width="9" height="16" rx="4" fill="#0f172a" />
        <rect x="128" y="52" width="9" height="16" rx="4" fill="#0f172a" />
        <path d="M67 66 Q70 78 84 76" stroke="#0f172a" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        <circle cx="85" cy="76" r="2.5" fill="#0f172a" />

        {/* body with a Z on its chest */}
        <rect x="78" y="82" width="44" height="30" rx="10" fill="#2D8CFF" />
        <path d="M92 90 H108 L92 104 H108" stroke="#ffffff" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />

        {/* typing arms */}
        <rect className="zm-arm-l" x="58" y="89" width="20" height="7" rx="3.5" fill="#1d6fe0" />
        <rect className="zm-arm-r" x="122" y="89" width="20" height="7" rx="3.5" fill="#1d6fe0" />
      </g>

      {/* the laptop it types on */}
      <path d="M52 118 L60 100 H140 L148 118 Z" fill="#334155" />
      <rect className="zm-screen" x="66" y="103" width="68" height="10" rx="2" fill="#38bdf8" opacity="0.85" />
      <rect x="70" y="106" width="20" height="2" rx="1" fill="#0b1f44" />
      <rect x="94" y="106" width="32" height="2" rx="1" fill="#0b1f44" />

      {/* the gear turning over its head */}
      <g className="zm-gear">
        <circle cx="156" cy="30" r="9" fill="none" stroke="#94a3b8" strokeWidth="5" strokeDasharray="4 3.1" />
        <circle cx="156" cy="30" r="4" fill="#94a3b8" />
      </g>

      {/* z's drifting up: zacking */}
      <text className="zm-z" x="36" y="56" fontSize="14" fontWeight="700" fill="#7dd3fc">z</text>
      <text className="zm-z zm-z2" x="44" y="50" fontSize="11" fontWeight="700" fill="#7dd3fc">z</text>
      <text className="zm-z zm-z3" x="30" y="48" fontSize="9" fontWeight="700" fill="#7dd3fc">z</text>
    </svg>
  );
}
